<template>
    <button type="button" :title="props.name"
        :class="{ loader: props.loading }"
        @click="click"
        ref="button"
    >
        <Icon :icon="props.icon" :sprite="props.sprite" />

        <Loader :inside="true"
            v-if="props.loading" />
    </button>
</template>

<script setup>
import { ref } from 'vue';
import Loader from '🔗/components/ui/Loader.vue';
import stores from '@/stores';

const others = stores.others();
const button = ref(null);

const click = () => {
    if (props.dunk && button.value) {
        button.value.classList.remove('dunk');
        setTimeout(() => {
            button.value.classList.add('dunk');
        }, 10);
    }
    others.vibrate();
}


const props = defineProps({
    icon: { type: String, default: '' },
    sprite: { type: String, default: '' },
    dunk: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    name: { type: String, default: '' }
});
</script>

<style lang="scss" scoped>
button {
    outline: none;
    height: 56px;
    width: 56px;
    gap: 0;
    display: flex;
    font-variant: unset;
    transition: all 0.4s;
    align-items: center;
    justify-content: center;
    background: var(--gradient-2);
    border: none;
    padding: 0;
    border-radius: 10px;
    cursor: pointer;

    svg {
        fill: var(--white-500);
        stroke: var(--white-80);
        width: 14px;
        height: 14px;
    }

    &[disabled] {
        cursor: default;
        filter: grayscale(1);
        opacity: .4;
    }

    &.disabled {
        filter: grayscale(1);
        opacity: .4;
    }

    &.round {
        border-radius: 50%;
    }

    &.size-xl {
        width: 70px;
        height: 70px;
        svg {
            width: 30px;
            height: 30px;
        }
    }

    &.size-l {
        width: 50px;
        height: 50px;
        svg {
            width: 22px;
            height: 22px;
        }

        &.icon-xl {
            svg {
                width: 30px;
                height: 30px;
            }
        }
    }

    &.size-m {
        width: 38px;
        height: 38px;
        svg {
            width: 14px;
            height: 14px;
        }
    }

    &.size-s {
        width: 32px;
        height: 32px;
        border-radius: 10px;
        flex-shrink: 0;
        svg {
            width: 14px;
            height: 14px;
        }
    }

    &.active {
        opacity: .8;
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
    }

    &.secondary {
        background-color: transparent;
        color: var(--dark-500);
        position: relative;
        background: var(--gradient-2) border-box;
        font-weight: 500;

        &:before {
            content: "";
            position: absolute;
            left: 1px;
            top: 1px;
            right: 1px;
            background: var(--white-500);
            bottom: 1px;
            border-radius: 10px;
        }

        svg  {
            z-index: 1;
            fill: var(--primary-500);
            stroke: var(--tertiary-500);
        }

        &.active {
            background: var(--light-10);
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
            svg {
                fill: var(--primary-300);
                stroke: var(--tertiary-500);
            }
        }
    }

    &.tertiary {
        background: transparent;
        svg {
            fill: var(--dark-500);
            stroke: var(--dark-80);
            transition: all .2s;
        }

        &.invert {
            svg {
                fill: var(--white-500);
                stroke: var(--white-80);
            }
        }

        &.active {
            background: var(--light-10);
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
            svg {
                fill: var(--primary-500);
                stroke: var(--tertiary-500);
                transform: scale(1.2);
            }

            &.invert {
                svg {
                    fill: var(--white-500);
                    stroke: var(--white-80);
                }
            }
        }

        &.solid {
            background: var(--dark-5);
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
            svg {
                fill: var(--dark-500);
                stroke: var(--dark-80);
                transform: scale(1.2);
            }

            &.invert {
                background: var(--light-10);
                svg {
                    fill: var(--white-500);
                    stroke: var(--white-80);
                }
            }
        }
    }

    &.loader {
        position: relative;
        overflow: hidden;
        & > * {
            opacity: 0;
        }

        & > .inside {
            position: absolute;
            left: 0;
            right: 0;
            top: 20%;
            bottom: 0;
            opacity: 1;
        }
    }

    &.animate-1 {
        @include animage-booble-shadow;
    }

    &.dunk {
        @include dunk;
    }

    &.dirty {
        position: relative;
        &:before {
            content: '';
            width: 0px;
            height: 0px;
            position: absolute;
            background: var(--white-90);
            box-shadow: 0px 0px 37px 22px var(--white-90);
            border-radius: 50%;
            z-index: -1;
            pointer-events: none;
        }

        &.invert {
            &:before {
                background: var(--dark-90);
                box-shadow: 0px 0px 37px 22px var(--dark-90);
            }
        }
    }
}

@media (hover: hover) and (pointer: fine) {
    button {
        &:hover {
            opacity: .8;
        }

        &.tertiary {
            &:hover {
                svg {
                    fill: var(--primary-500);
                    stroke: var(--tertiary-500);
                }

                &.invert {
                    background: var(--white-20);
                    svg {
                        fill: var(--white-500);
                        stroke: var(--white-80);
                    }
                }
            }
        }
    }
}

.direction-rtl button.rtl-sensitive {
    svg {
        transform: scaleX(-1);
    }
}
</style>
